import React, { useState, useEffect, useCallback } from 'react';
import {
  hrEmployeesApi,
  projectsAllApi,
  projectsTeamApi,
  projectsPlanningApi,
  type ProjectDto,
} from '../../services/api';
import AddTeamMemberModal, { type ProjectTeamMemberDto } from './AddTeamMemberModal';

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function totalProjectHours(planningItems: { project_id: string; duration_days: number }[], projectId: string): number {
  const totalDays = planningItems
    .filter((i) => i.project_id === projectId)
    .reduce((sum, i) => sum + (i.duration_days || 0), 0);
  return totalDays * 8;
}

export default function TeamCompositionPage() {
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [teamMembers, setTeamMembers] = useState<ProjectTeamMemberDto[]>([]);
  const [planningItems, setPlanningItems] = useState<{ project_id: string; duration_days: number }[]>([]);
  const [employeesById, setEmployeesById] = useState<Record<string, { department: string; job_title: string }>>({});
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingTeam, setLoadingTeam] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await projectsAllApi.list();
      if (res.data) setProjects(res.data);
      else setProjects([]);
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const loadTeamMembers = useCallback(async (projectId: string) => {
    if (!projectId) return;
    setLoadingTeam(true);
    try {
      const res = await projectsTeamApi.list(projectId);
      if (res.data) setTeamMembers(res.data);
      else setTeamMembers([]);
    } catch {
      setTeamMembers([]);
    } finally {
      setLoadingTeam(false);
    }
  }, []);

  const loadPlanningItems = useCallback(async (projectId: string) => {
    if (!projectId) return;
    try {
      const res = await projectsPlanningApi.list(projectId);
      if (res.data) {
        setPlanningItems(res.data.map((i) => ({ project_id: i.project_id, duration_days: i.duration_days })));
      } else {
        setPlanningItems([]);
      }
    } catch {
      setPlanningItems([]);
    }
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      loadTeamMembers(selectedProjectId);
      loadPlanningItems(selectedProjectId);
    } else {
      setTeamMembers([]);
      setPlanningItems([]);
    }
  }, [selectedProjectId, loadTeamMembers, loadPlanningItems]);

  useEffect(() => {
    hrEmployeesApi.list().then((res) => {
      if (res.data) {
        const map: Record<string, { department: string; job_title: string }> = {};
        res.data.forEach((e) => {
          map[e.id] = { department: e.department || '', job_title: e.job_title || '' };
        });
        setEmployeesById(map);
      }
    }).catch(() => {});
  }, []);

  const handleMemberAdded = (member: ProjectTeamMemberDto) => {
    setTeamMembers((prev) => [...prev, member]);
  };

  const handleMemberUpdated = async (member: ProjectTeamMemberDto) => {
    if (!selectedProjectId) return;
    try {
      const res = await projectsTeamApi.update(selectedProjectId, member.id, { hourly_rate: member.hourly_rate });
      if (res.data) {
        setTeamMembers((prev) => prev.map((m) => (m.id === member.id ? res.data! : m)));
      }
    } catch {
      /* ignore */
    }
  };

  const handleMemberRemoved = async (id: string) => {
    if (!selectedProjectId) return;
    try {
      const res = await projectsTeamApi.remove(selectedProjectId, id);
      if (res.success !== false) {
        setTeamMembers((prev) => prev.filter((m) => m.id !== id));
      }
    } catch {
      /* ignore */
    }
  };

  const openAddModal = () => {
    setModalOpen(true);
  };

  const projectTeam = teamMembers.filter((m) => m.project_id === selectedProjectId);
  const totalHours = selectedProjectId ? totalProjectHours(planningItems, selectedProjectId) : 0;
  const totalEstLabor = projectTeam.reduce((sum, m) => sum + totalHours * (m.hourly_rate || 0), 0);
  const avgHourlyRate = projectTeam.length > 0
    ? projectTeam.reduce((s, m) => s + (m.hourly_rate || 0), 0) / projectTeam.length
    : 0;

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Composition</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Assign employees to projects and track hourly rates for labor cost estimation
          </p>
        </div>
      </div>

      <div className="mb-4 shrink-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Project</label>
        <select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%20%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10"
        >
          <option value="">— Choose a project —</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.project_name}
              {p.client_name ? ` (${p.client_name})` : ''}
            </option>
          ))}
        </select>
        {loadingProjects && (
          <p className="text-sm text-gray-500 mt-2">Loading projects...</p>
        )}
        {!loadingProjects && projects.length === 0 && (
          <p className="text-sm text-amber-600 mt-2">No projects yet. Create a project first in All Projects.</p>
        )}
      </div>

      {selectedProjectId && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 shrink-0">
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Team Size</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{projectTeam.length}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours (Est.)</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{totalHours} hrs</p>
              <p className="text-xs text-gray-500 mt-0.5">From planning items</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Hourly Rate</p>
              <p className="text-xl font-bold text-brand-600 mt-1">{formatPeso(avgHourlyRate)}/hr</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Est. Labor Cost</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">{formatPeso(totalEstLabor)}</p>
              <p className="text-xs text-gray-500 mt-0.5">Hours × rates</p>
            </div>
          </div>

          <div className="flex justify-between items-center mb-3 shrink-0">
            <h2 className="font-semibold text-gray-900">Team Members & Hourly Rates</h2>
            <button
              type="button"
              onClick={openAddModal}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Member
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl shadow-card border border-gray-100">
            {loadingTeam ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-gray-500 text-sm">Loading team members...</p>
              </div>
            ) : projectTeam.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <p className="font-medium text-gray-700">No team members yet</p>
                <p className="text-sm text-gray-500 mt-1">Add employees to track hourly rates and labor cost</p>
                <button
                  type="button"
                  onClick={openAddModal}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add First Member
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Employee
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Department
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Job Title
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                        Est. Labor (₱)
                      </th>
                      <th className="w-24 py-3 px-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" scope="col">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {projectTeam.map((m) => {
                      const emp = employeesById[m.employee_id];
                      const department = (emp?.department || m.department || '').trim();
                      const jobTitle = (emp?.job_title || m.job_title || '').trim();
                      return (
                      <tr key={m.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium text-gray-900">{m.employee_name}</span>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{department || '—'}</td>
                        <td className="py-3 px-4 text-gray-600">{jobTitle || '—'}</td>
                        <td className="py-3 px-4 text-right font-medium text-gray-900">
                          {formatPeso(totalHours * (m.hourly_rate || 0))}
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              type="button"
                              onClick={() => {
                                const newRate = window.prompt('Enter new hourly rate (₱):', String(m.hourly_rate));
                                if (newRate !== null) {
                                  const rate = parseFloat(newRate);
                                  if (!isNaN(rate) && rate >= 0) {
                                    void handleMemberUpdated({ ...m, hourly_rate: rate });
                                  }
                                }
                              }}
                              className="p-2 rounded-lg text-gray-400 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                              title="Edit hourly rate"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Remove ${m.employee_name} from the team?`)) {
                                  void handleMemberRemoved(m.id);
                                }
                              }}
                              className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Remove from team"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <AddTeamMemberModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleMemberAdded}
        projectId={selectedProjectId}
        existingMembers={projectTeam}
      />
    </div>
  );
}
